import type { ComponentProps } from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv, type VariantProps } from "tailwind-variants";

import { focusRing } from "./interaction";

const iconButtonVariants = tv({
  base: [
    "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors",
    focusRing,
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
  ],
  variants: {
    size: {
      sm: "size-9",
      md: "size-11",
      lg: "size-12",
    },
    variant: {
      ghost: "text-content hover:bg-surface-hover hover:text-accent",
      subtle: "bg-surface-sunken text-content hover:bg-surface-muted hover:text-accent",
      outline:
        "border border-line-strong text-content hover:border-focus hover:bg-surface-hover hover:text-accent",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "ghost",
  },
});

export type IconButtonProps = {
  /* Icon-only controls carry no text, so the accessible name is required. */
  label: string;
  asChild?: boolean;
} & Omit<ComponentProps<"button">, "aria-label"> &
  VariantProps<typeof iconButtonVariants>;

export const IconButton = ({
  asChild = false,
  children,
  className,
  label,
  size,
  type = "button",
  variant,
  ...props
}: IconButtonProps) => {
  const sharedProps = {
    "aria-label": label,
    className: iconButtonVariants({ className, size, variant }),
    ...props,
  };

  /*
    asChild hands the element over to the child: it owns its own `type`. A
    <button> child inside a form must set type="button" itself, or it defaults
    to submit — Slot's props are typed as HTMLAttributes and cannot carry it.
  */
  if (asChild) {
    return <Slot {...sharedProps}>{children}</Slot>;
  }

  return (
    <button type={type} {...sharedProps}>
      {children}
    </button>
  );
};
